import React, { useMemo, useState } from 'react';
import { Container, TableSortLabel, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Table from 'components/Common/StyledTable';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';
import { notify } from 'components/Common/Toast';
import { dateFormat, formatAmount, timeFormat } from 'utils/helpers';
import { BasketActivityUser } from 'types/baskets';
import { resetTablePage } from 'redux/slices/common/appSlice';
import { AppDispatch, RootState } from 'redux/store';
import { selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { getProblemDetail, hasAtLeastRole } from 'api/v1Helpers';
import { refundDonation } from 'services/donationsApi';

function newIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `refund-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

interface ParishonerActivityProps {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- pre-existing prop signature
    navigationHandler: Function;
    data?: BasketActivityUser[];
    /** Re-fetch the activity list after a refund lands, so Refunded/Net reflect it. */
    onRefunded?: () => void;
}

function BasketActivityContainer({ navigationHandler, data, onRefunded }: ParishonerActivityProps) {
    const [searchText, setSearchText] = useState('');
    const dispatch = useDispatch<AppDispatch>();
    const effectiveRole = useSelector((state: RootState) => selectEffectiveStaffRole(state));
    const canRefund = hasAtLeastRole(effectiveRole, 'CLERK');

    const [refundTarget, setRefundTarget] = useState<BasketActivityUser | null>(null);
    const [refundAmountCents, setRefundAmountCents] = useState('0');
    const [refundIdempotencyKey, setRefundIdempotencyKey] = useState('');
    const [refundLoading, setRefundLoading] = useState(false);

    const filteredRows = useMemo(() => {
        const rows = data || [];
        if (!searchText.trim()) return rows;
        const text = searchText.trim().toLowerCase();
        return rows.filter((row) => row.userId?.toLowerCase().includes(text));
    }, [data, searchText]);

    // StyledTable paginates this list client-side off the shared table state and never clamps the
    // page to the row count, so a search run from page 2+ slices past the end of the filtered rows
    // and renders "no results" while matches exist. Reset the page on every change of the search
    // text — including clearing it, which shortens nothing but costs nothing either, and keeps the
    // rule "the visible page always belongs to the list currently on screen" with one branch.
    const handleSearchChange = (value: string) => {
        setSearchText(value);
        dispatch(resetTablePage());
    };

    const openRefund = (row: BasketActivityUser) => {
        setRefundTarget(row);
        setRefundAmountCents(String(row.netCents));
        setRefundIdempotencyKey(newIdempotencyKey());
    };

    const closeRefund = () => {
        if (refundLoading) return;
        setRefundTarget(null);
    };

    const handleConfirmRefund = async () => {
        if (!refundTarget || refundLoading) return;
        const amountCents = Number(refundAmountCents);
        if (!Number.isFinite(amountCents) || amountCents < 1) return;
        setRefundLoading(true);
        try {
            const result = await refundDonation(refundTarget.id, refundIdempotencyKey, amountCents);
            notify(
                result.settled ? 'Refund issued.' : `Refund submitted (status: ${result.status}). It may take a moment to reflect here.`,
                'success'
            );
            setRefundTarget(null);
            onRefunded?.();
        } catch (err) {
            notify(getProblemDetail(err) || 'Failed to issue refund', 'error');
        } finally {
            setRefundLoading(false);
        }
    };

    const columns = useMemo(
        () => [
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
            { id: 'createdAt', label: 'Date', render: ({ value }: any) => dateFormat(value) },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
            { id: 'createdTime', label: 'Time', render: ({ data }: any) => timeFormat(data?.['createdAt']) },
            { id: 'userId', label: 'Donor ID' },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
            { id: 'amountCents', label: 'Gift', render: ({ value }: any) => formatAmount(value, 'cents') },
            {
                id: 'refundedCents',
                label: 'Refunded',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
                render: ({ value }: any) => (value ? formatAmount(value, 'cents') : '—'),
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
            { id: 'netCents', label: 'Net', render: ({ value }: any) => formatAmount(value, 'cents') },
            ...(canRefund
                ? [
                      {
                          id: 'refund',
                          label: '',
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
                          render: ({ data }: any) => {
                              const row = data as BasketActivityUser;
                              if (row.netCents <= 0) return null;
                              return (
                                  <Button
                                      title="Refund"
                                      variant="outlined"
                                      sx={{ minWidth: 90 }}
                                      onClick={(e: React.MouseEvent) => {
                                          e.stopPropagation();
                                          openRefund(row);
                                      }}
                                  />
                              );
                          },
                      },
                  ]
                : []),
        ],
        [canRefund]
    );

    return (
        <Container sx={{ backgroundColor: 'white' }}>
            <div style={{ paddingTop: 8, marginTop: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 12 }}>
                    <TextInput
                        backgroundColor="#F2F2F6"
                        height={50}
                        sx={{ mr: 2, color: '#F2F2F6' }}
                        width={'30%'}
                        icon={null}
                        placeholder="Total Gifts:"
                    />
                    <TextInput backgroundColor="#F2F2F6" height={50} width={'30%'} icon={null} placeholder="Last Auto Clear:" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                        <Typography>Recent Activity</Typography>
                        <TableSortLabel sx={{ fontSize: 12, color: '#374151' }}>Today</TableSortLabel>
                    </div>
                    <TextInput
                        value={searchText}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        height={40}
                        width={300}
                        backgroundColor="#F2F2F6"
                        sx={{ ml: 2, fontSize: 20, fontWeight: 400 }}
                        placeholder="Search donor ID"
                    />
                </div>
                <Table
                    successValue="Complete"
                    // No working detail page exists for a single contribution — GET /v1/donations/:id
                    // is donor-scoped only, so there is nothing to navigate to. The Refund column
                    // above needs no such page: it acts on the row's own already-known id/amount.
                    disableRowNavigation
                    navigationHandler={navigationHandler}
                    columns={columns}
                    data={filteredRows}
                    totalCount={filteredRows.length}
                />
            </div>

            <Modal onClose={closeRefund} isOpen={!!refundTarget} modalTitle="Issue Refund">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>
                        Refund donor <strong>{refundTarget?.userId}</strong>&apos;s gift of{' '}
                        <strong>{formatAmount(refundTarget?.amountCents || 0, 'cents')}</strong>. This cannot be undone.
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 12, sm: 14 }, mt: 1, color: 'text.secondary' }}>
                        Remaining refundable amount: {formatAmount(refundTarget?.netCents || 0, 'cents')}.
                    </Typography>
                    <TextInput
                        type="currency"
                        icon={null}
                        sx={{ mt: 3 }}
                        width="100%"
                        label="Refund amount"
                        placeholder="$0.00"
                        value={formatAmount(refundAmountCents, 'cents')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRefundAmountCents(e.target.value)}
                        disabled={refundLoading}
                    />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={closeRefund} width="45%" title="Cancel" disabled={refundLoading} variant="outlined" />
                        <Button
                            onClick={handleConfirmRefund}
                            width="45%"
                            title="Confirm Refund"
                            loading={refundLoading}
                            disabled={refundLoading || !Number(refundAmountCents)}
                        />
                    </div>
                </>
            </Modal>
        </Container>
    );
}

export default BasketActivityContainer;
